import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export const PremierUpgradeModal = ({ isOpen, onClose, onUpgrade }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (isOpen) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      setUser(userData);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCheckout = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Create Stripe Checkout session
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            auctioneer_id: user.id
          })
        }
      );
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create checkout session');
      }
      
      const { session_id } = await response.json();
      
      // Redirect to Stripe Checkout
      const stripe = await stripePromise;
      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId: session_id
      });
      
      if (stripeError) {
        throw new Error(stripeError.message);
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">🏆 Upgrade to Premier</h2>
              <p className="text-lg text-green-600 font-semibold mt-1">Only $4.99/month - Billed annually at $59.88</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Benefits */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 mb-6">
            <h3 className="font-bold text-lg mb-4">What You Get:</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start">
                <span className="text-2xl mr-3">🎯</span>
                <div>
                  <p className="font-semibold">Top Search Position</p>
                  <p className="text-sm text-gray-600">Appear first in local searches</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-2xl mr-3">🏅</span>
                <div>
                  <p className="font-semibold">Premier Badge</p>
                  <p className="text-sm text-gray-600">Stand out with verified badge</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-2xl mr-3">🎨</span>
                <div>
                  <p className="font-semibold">Custom Logo & Video</p>
                  <p className="text-sm text-gray-600">Showcase your brand</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-2xl mr-3">📍</span>
                <div>
                  <p className="font-semibold">Featured in Regional Ads</p>
                  <p className="text-sm text-gray-600">Sidebar placement in your area</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-2xl mr-3">🤖</span>
                <div>
                  <p className="font-semibold">AI Search Visibility</p>
                  <p className="text-sm text-gray-600">Get found in ChatGPT & AI searches</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-2xl mr-3">⭐</span>
                <div>
                  <p className="font-semibold">Review Integration</p>
                  <p className="text-sm text-gray-600">Display Google & Yelp reviews</p>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing Summary */}
          <div className="border-2 border-green-600 rounded-lg p-6 bg-green-50 mb-6">
            <h4 className="font-bold text-lg mb-3">💳 Subscription Details</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Premier Listing (Annual)</span>
                <span className="font-semibold">$59.88/year</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Monthly equivalent</span>
                <span>$4.99/month</span>
              </div>
              <div className="border-t pt-2 mt-2 flex justify-between font-bold text-lg">
                <span>Total Today</span>
                <span className="text-green-600">$59.88</span>
              </div>
              <div className="text-xs text-gray-600 mt-3">
                <p>✓ Automatically renews annually</p>
                <p>✓ Cancel anytime from your dashboard</p>
                <p>✓ Secure payment via Stripe</p>
              </div>
            </div>
          </div>

          {/* After Payment Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-blue-900 mb-2">📝 After Payment</h4>
            <p className="text-sm text-blue-800">
              Once your payment is complete, you'll be able to customize your Premier profile with:
            </p>
            <ul className="text-sm text-blue-800 mt-2 space-y-1">
              <li>• Upload your custom logo</li>
              <li>• Add a promotional video or YouTube link</li>
              <li>• Write a marketing tagline</li>
              <li>• Add links to your review profiles</li>
            </ul>
          </div>

          {/* CTA Button */}
          <button
            onClick={handleCheckout}
            disabled={loading}
            className="w-full px-6 py-4 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition text-lg shadow-lg"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
            ) : (
              '💳 Proceed to Secure Payment'
            )}
          </button>
          
          <p className="text-xs text-gray-500 text-center mt-3">
            🔒 Secure checkout powered by Stripe
          </p>
        </div>
      </div>
    </div>
  );
};
